import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  BookOpen, Plus, Search, Filter, Share2, Download, Upload, 
  CheckCircle, XCircle, AlertTriangle, ArrowRight, Trash2, Edit, Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DeckManagerPage = () => {
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("my-decks");
  const [formats, setFormats] = useState([]);

  useEffect(() => {
    loadDecks();
    loadFormats();
  }, []);

  const loadDecks = async () => {
    try {
      const res = await axios.get(`${API}/decks`, { withCredentials: true });
      setDecks(res.data.decks || []);
    } catch (e) {
      console.error("Error loading decks:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadFormats = async () => {
    try {
      const res = await axios.get(`${API}/decks/formats`);
      setFormats(res.data.formats || []);
    } catch (e) {
      console.error("Error loading formats:", e);
    }
  };

  const deleteDeck = async (deckId) => {
    if (!window.confirm("¿Estás seguro de eliminar este deck?")) return;
    try {
      await axios.delete(`${API}/decks/${deckId}`, { withCredentials: true });
      setDecks(decks.filter(d => d.id !== deckId));
    } catch (e) {
      alert("Error al eliminar el deck");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-purple-950/30 to-black pt-24 pb-12" data-testid="deck-manager-page">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black mb-2">
            <span className="bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">
              Sistema de Decks
            </span>
          </h1>
          <p className="text-gray-400">Crea, guarda y compara tus mazos entre formatos</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 justify-center">
          {[
            { id: "my-decks", label: "Mis Decks", icon: <BookOpen size={18} /> },
            { id: "compare", label: "Comparador", icon: <ArrowRight size={18} /> },
            { id: "import", label: "Importar", icon: <Upload size={18} /> },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                activeTab === tab.id
                  ? "bg-yellow-500 text-black"
                  : "bg-purple-900/50 text-gray-400 hover:text-white"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="glass-card rounded-2xl p-6">
          {activeTab === "my-decks" && (
            <MyDecksTab decks={decks} loading={loading} formats={formats} onDelete={deleteDeck} onRefresh={loadDecks} />
          )}
          {activeTab === "compare" && (
            <CompareTab formats={formats} />
          )}
          {activeTab === "import" && (
            <ImportTab formats={formats} onImported={loadDecks} />
          )}
        </div>
      </div>
    </div>
  );
};

const MyDecksTab = ({ decks, loading, formats, onDelete, onRefresh }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-12 h-12 border-4 border-t-yellow-500 border-purple-700 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-400">Cargando decks...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white">Mis Decks ({decks.length}/10)</h2>
        <Link 
          to="/deck-builder/new" 
          className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg font-semibold hover:bg-yellow-400 transition-colors"
          data-testid="create-deck-btn"
        >
          <Plus size={18} />
          Nuevo Deck
        </Link>
      </div>

      {decks.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen size={48} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400 mb-4">No tienes decks guardados</p>
          <Link to="/deck-builder/new" className="text-yellow-400 hover:text-yellow-300">
            Crear mi primer deck →
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {decks.map((deck) => (
            <DeckCard key={deck.id} deck={deck} formats={formats} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
};

const DeckCard = ({ deck, formats, onDelete }) => {
  const formatInfo = formats.find(f => f.id === deck.format);
  
  const formatColors = {
    goat: "from-green-500 to-emerald-600",
    edison: "from-yellow-500 to-amber-600",
    hat: "from-purple-500 to-indigo-600",
    speed_duel: "from-blue-500 to-cyan-600",
    tcg_current: "from-pink-500 to-rose-600",
  };

  return (
    <div className="bg-purple-900/30 rounded-xl overflow-hidden hover:bg-purple-800/40 transition-colors group" data-testid={`deck-card-${deck.id}`}>
      {/* Format Header */}
      <div className={`bg-gradient-to-r ${formatColors[deck.format] || "from-gray-500 to-gray-600"} px-4 py-2`}>
        <span className="text-white font-bold text-sm">{formatInfo?.name || deck.format}</span>
      </div>
      
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-bold text-white group-hover:text-yellow-400 transition-colors">{deck.name}</h3>
          {deck.is_public && (
            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded flex items-center gap-1">
              <Share2 size={12} />
              Público
            </span>
          )}
        </div>
        
        {deck.description && (
          <p className="text-gray-400 text-sm mb-3 line-clamp-2">{deck.description}</p>
        )}
        
        {/* Deck Stats */}
        <div className="flex gap-2 text-xs text-gray-500 mb-4">
          <span>Main: {deck.main_deck?.length || 0}</span>
          <span>Extra: {deck.extra_deck?.length || 0}</span>
          <span>Side: {deck.side_deck?.length || 0}</span>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2">
          <Link 
            to={`/deck-builder/${deck.id}`}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-purple-700/50 rounded-lg text-gray-300 hover:bg-purple-600/50 hover:text-white transition-colors text-sm"
          >
            <Eye size={14} />
            Ver
          </Link>
          <Link 
            to={`/deck-builder/${deck.id}`}
            className="flex items-center justify-center px-3 py-2 bg-blue-600/30 rounded-lg text-blue-400 hover:bg-blue-600/50 transition-colors"
          >
            <Edit size={14} />
          </Link>
          <button 
            onClick={() => onDelete(deck.id)}
            className="flex items-center justify-center px-3 py-2 bg-red-600/30 rounded-lg text-red-400 hover:bg-red-600/50 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const CompareTab = ({ formats }) => {
  const [cardList, setCardList] = useState("");
  const [sourceFormat, setSourceFormat] = useState("goat");
  const [targetFormat, setTargetFormat] = useState("edison");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCompare = async () => {
    const cards = cardList.split("\n").map(c => c.trim()).filter(c => c.length > 0);
    if (cards.length === 0) {
      alert("Ingresa al menos una carta");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/decks/compare`, {
        deck_cards: cards,
        source_format: sourceFormat,
        target_format: targetFormat
      });
      setResults(res.data);
    } catch (e) {
      alert("Error al comparar: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (transition) => {
    switch (transition) {
      case "legal": return <CheckCircle size={16} className="text-green-400" />;
      case "limited": return <AlertTriangle size={16} className="text-yellow-400" />;
      case "semi_limited": return <AlertTriangle size={16} className="text-orange-400" />;
      case "forbidden": return <XCircle size={16} className="text-red-400" />;
      case "not_released": return <XCircle size={16} className="text-gray-400" />;
      default: return <XCircle size={16} className="text-gray-600" />;
    }
  };

  const getStatusBadge = (transition) => {
    const styles = {
      legal: "bg-green-500/20 text-green-400",
      limited: "bg-yellow-500/20 text-yellow-400",
      semi_limited: "bg-orange-500/20 text-orange-400",
      forbidden: "bg-red-500/20 text-red-400",
      not_released: "bg-gray-500/20 text-gray-400",
      not_found: "bg-gray-700/20 text-gray-500",
    };
    const labels = {
      legal: "Legal",
      limited: "Limitada",
      semi_limited: "Semi-Limitada",
      forbidden: "Prohibida",
      not_released: "No disponible",
      not_found: "No encontrada",
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[transition] || styles.not_found}`}>
        {labels[transition] || transition}
      </span>
    );
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-4">Comparador de Formatos</h2>
      <p className="text-gray-400 mb-6">
        Compara la legalidad de tu deck entre diferentes formatos. Ingresa los nombres de las cartas (una por línea).
      </p>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        {/* Input Section */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Lista de Cartas</label>
          <textarea
            value={cardList}
            onChange={(e) => setCardList(e.target.value)}
            placeholder="Dark Magician&#10;Pot of Greed&#10;Black Luster Soldier - Envoy of the Beginning&#10;Tour Guide From the Underworld"
            className="w-full h-48 px-4 py-3 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 resize-none"
            data-testid="card-list-input"
          />
        </div>

        {/* Format Selection */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Formato de Origen</label>
            <select
              value={sourceFormat}
              onChange={(e) => setSourceFormat(e.target.value)}
              className="w-full px-4 py-3 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white focus:outline-none focus:border-yellow-500"
              data-testid="source-format-select"
            >
              {formats.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>

          <div className="flex justify-center">
            <ArrowRight size={24} className="text-yellow-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Formato Destino</label>
            <select
              value={targetFormat}
              onChange={(e) => setTargetFormat(e.target.value)}
              className="w-full px-4 py-3 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white focus:outline-none focus:border-yellow-500"
              data-testid="target-format-select"
            >
              {formats.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </div>

          <button
            onClick={handleCompare}
            disabled={loading}
            className="w-full px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold rounded-lg hover:from-yellow-400 hover:to-amber-400 transition-all disabled:opacity-50"
            data-testid="compare-btn"
          >
            {loading ? "Comparando..." : "Comparar Deck"}
          </button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="border-t border-purple-700/50 pt-6" data-testid="compare-results">
          {/* Summary */}
          <div className={`p-4 rounded-xl mb-6 ${results.deck_compatible ? "bg-green-500/20 border border-green-500/50" : "bg-red-500/20 border border-red-500/50"}`}>
            <div className="flex items-center gap-3">
              {results.deck_compatible ? (
                <CheckCircle size={24} className="text-green-400" />
              ) : (
                <XCircle size={24} className="text-red-400" />
              )}
              <div>
                <h3 className={`font-bold ${results.deck_compatible ? "text-green-400" : "text-red-400"}`}>
                  {results.deck_compatible ? "Deck Compatible" : "Deck Incompatible"}
                </h3>
                <p className="text-sm text-gray-300">
                  {results.summary.fully_legal} legal, {results.summary.limited_in_target} limitadas, 
                  {results.summary.forbidden_in_target} prohibidas, {results.summary.not_released_in_target} no disponibles
                </p>
              </div>
            </div>
          </div>

          {/* Card List */}
          <div className="space-y-2">
            {results.cards.map((card, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-purple-900/30 rounded-lg p-3">
                {card.image && (
                  <img src={card.image} alt={card.name} className="w-10 h-14 object-cover rounded" />
                )}
                <div className="flex-1">
                  <p className="text-white font-medium">{card.name}</p>
                  <p className="text-xs text-gray-500">{card.card_type}</p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(card.transition)}
                  {getStatusBadge(card.transition)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ImportTab = ({ formats, onImported }) => {
  const [ydkContent, setYdkContent] = useState("");
  const [deckName, setDeckName] = useState("");
  const [format, setFormat] = useState("tcg_current");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    if (!ydkContent.trim()) {
      alert("Pega el contenido del archivo YDK");
      return;
    }
    if (!deckName.trim()) {
      alert("Ingresa un nombre para el deck");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/decks/import/ydk`, {
        ydk: ydkContent,
        name: deckName,
        format: format
      }, { withCredentials: true });
      setResult(res.data);
      onImported();
    } catch (e) {
      alert("Error al importar: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-white mb-4">Importar Deck (YDK)</h2>
      <p className="text-gray-400 mb-6">
        Pega el contenido de un archivo .ydk para importar tu deck.
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Nombre del Deck</label>
          <input
            type="text"
            value={deckName}
            onChange={(e) => setDeckName(e.target.value)}
            placeholder="Mi Deck Importado"
            className="w-full px-4 py-3 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
            data-testid="deck-name-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Formato</label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            className="w-full px-4 py-3 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            data-testid="import-format-select"
          >
            {formats.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Contenido YDK</label>
          <textarea
            value={ydkContent}
            onChange={(e) => setYdkContent(e.target.value)}
            placeholder="#created by...&#10;#main&#10;46986414&#10;46986414&#10;46986414&#10;#extra&#10;!side"
            className="w-full h-48 px-4 py-3 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500 resize-none font-mono text-sm"
            data-testid="ydk-content-input"
          />
        </div>

        <button
          onClick={handleImport}
          disabled={loading}
          className="w-full px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold rounded-lg hover:from-yellow-400 hover:to-amber-400 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="import-btn"
        >
          <Upload size={18} />
          {loading ? "Importando..." : "Importar Deck"}
        </button>
      </div>

      {result && (
        <div className="mt-6 p-4 bg-green-500/20 border border-green-500/50 rounded-xl" data-testid="import-result">
          <div className="flex items-center gap-3">
            <CheckCircle size={24} className="text-green-400" />
            <div>
              <h3 className="font-bold text-green-400">Deck Importado Exitosamente</h3>
              <p className="text-sm text-gray-300">
                Main: {result.stats.main_deck} | Extra: {result.stats.extra_deck} | Side: {result.stats.side_deck}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeckManagerPage;
